/**
 * ================================================================================================
 *  THE WAY OUT OF THE PANEL, AND IT IS NO LONGER A MAILBOX
 * ================================================================================================
 *
 * The Owner, 2026-11-04:
 *
 *     *"„לא נפתר" או „אחר" פותחים טקסט חופשי, ובאישור תיפתח פנייה אמיתית בתור התמיכה … תנאי
 *      מסירה: פנייה מבוקרת אחת עוברת מהבוט לתור ולוח עם מספר וניתן לשבץ אותה. אם בפועל נפתח
 *      רק חלון מייל, דווח שהפנייה עדיין לא נקלטה."*
 *
 * ── WHAT WAS THERE, AND WHAT HE ASKED ME TO CHECK ─────────────────────────────────────────────
 *
 * He said `info@rr-cpa.co.il` was a guess. IT WAS NOT THAT ADDRESS AND IT WAS NOT HARD-CODED:
 * `ContactPath` built a `mailto:` to whatever `GLPOINT_MAIL_REPLY_TO` holds on the estate, and
 * when that key is empty the panel said there was nowhere to write rather than substituting the
 * sending address — which `MailIdentity` itself calls *"routinely unmonitored"*.
 *
 * AND THE COMMENT ABOVE IT NAMED THIS FILE BEFORE IT EXISTED: *"The alternative was a route that
 * posts a message: a capability, a store, a queue and a mailbox somebody has to watch — and until
 * somebody watches it, a form that swallows a customer's problem is worse than no form … When the
 * Owner wants a tracked queue instead, that is a decision with an owner and a mailbox, not a
 * component."* He has made that decision, so the queue arrives WITH its reader: 0055 ships the
 * board, the assignment and the response target in the same release.
 *
 * ── THE MAILBOX IS STILL HERE, FOR THE TWO CASES A TICKET CANNOT BE RAISED ────────────────────
 *
 * A ticket needs an office and a capability. A platform administrator signed in on the console has
 * a user and no office, and a session without `support.ticket.open` cannot post. In both the panel
 * falls back to the address — and SAYS which path the reader is on, because *"my enquiry was
 * received"* and *"I sent an e-mail"* are different facts about whether anybody is tracking it.
 *
 * ── AND IT NEVER CLAIMS SOMEBODY IS LOOKING AT IT ─────────────────────────────────────────────
 *
 * The confirmation says the ticket was OPENED, with its number and the response target the server
 * stamped. It does not say anybody has read it: nothing in this product knows that, and the
 * target is a target.
 */

import { useState } from 'react';

import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { SupportReference } from '../components/SupportReference';
import { routes } from '../routes';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';

/** 0055's own floor. A ticket reading "help" costs somebody a round trip to ask what about. */
const MIN_BODY = 10;
const MAX_BODY = 4000;

interface Opened {
  readonly displayNo: string;
  readonly respondByAt: string;
  /** False when an identical live ticket already existed and this press created nothing. */
  readonly created: boolean;
}

export function TicketPath({
  subject,
  screenId,
  canOpen,
  mailAddress,
  reference,
}: {
  /**
   * THE BOT'S OWN TOPIC LABEL — the category the reader was in, or their question.
   *
   * It is passed in rather than typed, because the panel already knows it and asking a person to
   * restate what they just clicked is how a form gets abandoned. Nothing is GENERATED: it is the
   * GUIDE's own category text or the words the reader submitted.
   */
  readonly subject: string;
  readonly screenId: string | undefined;
  /**
   * WHETHER THIS SESSION CAN RAISE A TICKET AT ALL — an office, and `support.ticket.open`.
   *
   * False is a real and ordinary state: a platform administrator on the console has no office to
   * raise one for. The panel then offers the address and says so, rather than showing a form that
   * the server would refuse.
   */
  readonly canOpen: boolean;
  /** `GLPOINT_MAIL_REPLY_TO`, from `whoami`. `null` on an estate with no transport configured. */
  readonly mailAddress: string | null;
  readonly reference: string;
}): JSX.Element {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);

  const typed = text.trim();
  const ready = typed.length >= MIN_BODY && typed.length <= MAX_BODY;

  async function open(): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      const result = await callOperation('tenant.support_ticket.open', {
        body: {
          subject,
          body: typed,
          /*
           * `?? null` AND NOT OMITTED. The shell's `screenId` is optional and the body's field is
           * `string | null`: under `exactOptionalPropertyTypes` those are different types, and a
           * missing key would reach the server as an absent field rather than a stated "unknown".
           */
          screenId: screenId ?? null,
        },
      });
      setOpened({
        displayNo: result.displayNo,
        respondByAt: result.respondByAt,
        created: result.created,
      });
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSending(false);
    }
  }

  if (opened !== null) {
    return (
      <section className="assistant__block" data-area="ticket-opened">
        <h3 className="assistant__heading">
          {opened.created ? 'הפנייה נפתחה' : 'הפנייה הזאת כבר פתוחה'}
        </h3>
        <p>
          {'מספר פנייה: '}
          <strong dir="ltr">{opened.displayNo}</strong>
        </p>
        {opened.created ? null : (
          /*
           * *"קבלה חוזרת של אותה הודעה לא תיצור פנייה כפולה."* The second press is not an error and
           * it is not a second ticket: the reader is told which ticket already holds their words, so
           * they can quote the same number rather than wondering which of two is live.
           */
          <p>שליחה חוזרת של אותה פנייה אינה פותחת פנייה נוספת. המספר שלמעלה הוא הפנייה הקיימת.</p>
        )}
        <p>
          {'יעד התגובה שנרשם: '}
          <span dir="ltr">{opened.respondByAt}</span>
        </p>
        {/*
          A TARGET IS A TARGET. The panel does not say anybody has read it -- nothing in this
          product knows that -- and saying so is what keeps the number honest.
        */}
        <p>היעד הוא מועד שנרשם בפנייה. אין במערכת אישור שמישהו כבר קרא אותה.</p>
        {/*
          ── והיכן למצוא אותה אחר כך. **2026-11-04.** ────────────────────────────────

          A number on a confirmation is only useful if it leads somewhere. Until this round there
          was nowhere: the office surface had one support operation, the POST that produced this
          very panel, and closing the tab ended the reader's relationship with their own enquiry.

          The link is here and not only in the navigation because THIS is the moment somebody
          wants it -- they have just been handed a number and are deciding whether to write it
          down.
        */}
        <p>
          <a href={routes.tenantEnquiries}>הפניות שלי</a>
          {' — למעקב אחר הפנייה הזו ואחר קודמות.'}
        </p>
        <SupportReference requestUid={reference} />
      </section>
    );
  }

  if (!canOpen) {
    return (
      <section className="assistant__block" data-area="contact">
        <h3 className="assistant__heading">פנייה לתמיכה</h3>
        {/*
          ── IT SAYS WHICH PATH THE READER IS ON, AND THAT IS THE POINT ────────────────────────

          *"אם בפועל נפתח רק חלון מייל, דווח שהפנייה עדיין לא נקלטה."* A mail window is not an
          intake: nobody is tracking it, there is no number and there is no target. The reader is
          told that in words, because "I wrote to support" and "my enquiry was received" are
          different facts and only one of them has a queue behind it.
        */}
        <p>
          מהחשבון הזה לא ניתן לפתוח פנייה במערכת: פתיחת פנייה היא פעולה של ארגון, והחשבון הזה אינו
          משויך לארגון או שאין לו ההרשאה לכך.
        </p>
        {mailAddress === null ? (
          <p className="assistant__withheld">
            בשרת הזה גם לא מוגדרת כתובת דואר לתמיכה, ולכן אין כאן דרך לפנות. יש לפנות דרך חשבון של
            ארגון.
          </p>
        ) : (
          <>
            <p>
              אפשר לשלוח דואר לכתובת התמיכה. <strong>הודעת דואר אינה נקלטת בתור</strong>: לא נפתחת
              פנייה, אין מספר ואין יעד תגובה.
            </p>
            {/*
              `btn--nav` AND NOT `btn--primary`. `actionAffordance.test.ts`: *"NAVIGATION IS NOT AN
              ACTION"* -- and this one leaves the product entirely, which is the strongest case for
              that rule rather than an exception to it.

              AND THE PARAGRAPH IS OUTSIDE THE TAG because the check reads the text that FOLLOWS
              `<a`, so a comment between the tag and its attributes naming the forbidden class is a
              finding about itself.
            */}
            <a
              className="btn btn--nav"
              href={`mailto:${mailAddress}?subject=${encodeURIComponent(`GL-POINT ${reference}`)}`}
            >
              שליחת דואר לתמיכה
            </a>
          </>
        )}
        <SupportReference requestUid={reference} />
      </section>
    );
  }

  return (
    <section className="assistant__block" data-area="ticket">
      <h3 className="assistant__heading">פתיחת פנייה לתמיכה</h3>
      {failure === null ? null : <ActionFailureNotice failure={failure} />}
      <p>
        {'הנושא שיירשם: '}
        <strong>{subject}</strong>
      </p>
      <label className="field">
        <span className="field__label">מה קרה, במילים שלך</span>
        <textarea
          className="field__input"
          name="support-ticket-body"
          rows={4}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <span className="field__hint">
          {`לפחות ${MIN_BODY} תווים. מה ניסית לעשות, מה קרה בפועל ומה ציפית שיקרה — זה מה שחוסך סבב שאלות.`}
        </span>
      </label>
      {/*
        THE SCREEN AND THE VERSION ARE STATED, NOT SMUGGLED. They travel with the ticket, and a
        reader who is about to press send has a right to know what else goes with their words --
        especially because the answer is "nothing about your data".
      */}
      <p className="field__hint">
        עם הפנייה נשלחים: המסך שאתה נמצא בו, שם הארגון ושמך, וגרסת המערכת — שאותה רושם השרת
        ולא הדפדפן. לא נשלחים נתונים מתוך המערכת.
      </p>
      <div className="actions-row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={!ready || sending}
          onClick={() => void open()}
        >
          {sending ? 'פותח פנייה…' : 'פתיחת פנייה'}
        </button>
      </div>
      <SupportReference requestUid={reference} />
    </section>
  );
}
